import React, { useState } from 'react';
import { Palette, Upload, Eye, Save, RotateCcw } from 'lucide-react';

const BrandThemingPage = () => {
  const [branding, setBranding] = useState({
    logo: null,
    logoPreview: null,
    companyName: 'ScopeOps',
    primaryColor: '#3b82f6',
    secondaryColor: '#1e40af',
    accentColor: '#10b981',
    reportHeader: 'ScopeOps - Portfolio Management',
    reportFooter: 'Confidential - Internal Use Only',
    dateFormat: 'DD/MM/YYYY',
    timezone: 'Africa/Johannesburg'
  });

  const [hasChanges, setHasChanges] = useState(false);

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setBranding(prev => ({
          ...prev,
          logo: file,
          logoPreview: reader.result
        }));
        setHasChanges(true);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = () => {
    // TODO: Implement API call to save branding
    setHasChanges(false);
    alert('Branding saved successfully!');
  };

  const colorPresets = [
    { name: 'Blue', primary: '#3b82f6', secondary: '#1e40af' },
    { name: 'Green', primary: '#10b981', secondary: '#059669' },
    { name: 'Purple', primary: '#8b5cf6', secondary: '#6d28d9' },
    { name: 'Red', primary: '#ef4444', secondary: '#dc2626' },
    { name: 'Orange', primary: '#f97316', secondary: '#ea580c' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--clr-text-primary)] flex items-center gap-3">
            <Palette className="w-7 h-7 text-pink-500" />
            Brand & Theming
          </h1>
          <p className="text-[var(--clr-text-secondary)] mt-1">Customize the look and feel of your ScopeOps instance</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setHasChanges(false)}
            className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-[var(--clr-surface-hover)] flex items-center gap-2"
            data-testid="brand-reset-btn"
          >
            <RotateCcw className="w-4 h-4" />
            Reset
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges}
            className="px-4 py-2 bg-[var(--clr-brand-primary)] text-white rounded-lg hover:bg-[var(--clr-brand-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            data-testid="brand-save-btn"
          >
            <Save className="w-4 h-4" />
            Save Changes
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Logo & Company */}
        <div className="bg-[var(--clr-surface-card)] rounded-xl border shadow-[var(--shadow-card)]">
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold text-[var(--clr-text-primary)]">Logo & Identity</h2>
          </div>
          <div className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Company Logo</label>
              <div className="flex items-center gap-4">
                <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center overflow-hidden bg-[var(--clr-surface-subtle)]">
                  {branding.logoPreview ? (
                    <img src={branding.logoPreview} alt="Logo preview" className="w-full h-full object-contain" />
                  ) : (
                    <Upload className="w-8 h-8 text-[var(--clr-text-muted)]" />
                  )}
                </div>
                <div>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="logo-upload"
                    data-testid="logo-upload-input"
                  />
                  <label
                    htmlFor="logo-upload"
                    className="px-4 py-2 bg-[var(--clr-surface-subtle)] text-gray-700 rounded-lg hover:bg-gray-200 cursor-pointer inline-block"
                  >
                    Upload Logo
                  </label>
                  <p className="text-xs text-[var(--clr-text-muted)] mt-2">PNG, JPG up to 2MB. Recommended: 200x200px</p>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Company Name</label>
              <input
                type="text"
                value={branding.companyName}
                onChange={(e) => {
                  setBranding(prev => ({ ...prev, companyName: e.target.value }));
                  setHasChanges(true);
                }}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-[var(--clr-brand-primary)] focus:border-blue-500"
                data-testid="company-name-input"
              />
            </div>
          </div>
        </div>

        {/* Color Theme */}
        <div className="bg-[var(--clr-surface-card)] rounded-xl border shadow-[var(--shadow-card)]">
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold text-[var(--clr-text-primary)]">Color Theme</h2>
          </div>
          <div className="p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Quick Presets</label>
              <div className="flex flex-wrap gap-2">
                {colorPresets.map((preset) => (
                  <button
                    key={preset.name}
                    onClick={() => {
                      setBranding(prev => ({
                        ...prev,
                        primaryColor: preset.primary,
                        secondaryColor: preset.secondary
                      }));
                      setHasChanges(true);
                    }}
                    className="px-3 py-1 rounded-full border hover:border-gray-400 flex items-center gap-2"
                    data-testid={`preset-${preset.name.toLowerCase()}`}
                  >
                    <div className="w-4 h-4 rounded-full" style={{ backgroundColor: preset.primary }} />
                    <span className="text-sm">{preset.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Primary</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={branding.primaryColor}
                    onChange={(e) => {
                      setBranding(prev => ({ ...prev, primaryColor: e.target.value }));
                      setHasChanges(true);
                    }}
                    className="w-10 h-10 rounded cursor-pointer"
                    data-testid="primary-color-input"
                  />
                  <input
                    type="text"
                    value={branding.primaryColor}
                    onChange={(e) => {
                      setBranding(prev => ({ ...prev, primaryColor: e.target.value }));
                      setHasChanges(true);
                    }}
                    className="flex-1 px-2 py-1 border rounded text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Secondary</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={branding.secondaryColor}
                    onChange={(e) => {
                      setBranding(prev => ({ ...prev, secondaryColor: e.target.value }));
                      setHasChanges(true);
                    }}
                    className="w-10 h-10 rounded cursor-pointer"
                    data-testid="secondary-color-input"
                  />
                  <input
                    type="text"
                    value={branding.secondaryColor}
                    onChange={(e) => {
                      setBranding(prev => ({ ...prev, secondaryColor: e.target.value }));
                      setHasChanges(true);
                    }}
                    className="flex-1 px-2 py-1 border rounded text-sm"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Accent</label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={branding.accentColor}
                    onChange={(e) => {
                      setBranding(prev => ({ ...prev, accentColor: e.target.value }));
                      setHasChanges(true);
                    }}
                    className="w-10 h-10 rounded cursor-pointer"
                    data-testid="accent-color-input"
                  />
                  <input
                    type="text"
                    value={branding.accentColor}
                    onChange={(e) => {
                      setBranding(prev => ({ ...prev, accentColor: e.target.value }));
                      setHasChanges(true);
                    }}
                    className="flex-1 px-2 py-1 border rounded text-sm"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Report Settings */}
        <div className="bg-[var(--clr-surface-card)] rounded-xl border shadow-[var(--shadow-card)]">
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold text-[var(--clr-text-primary)]">Report Settings</h2>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Report Header Text</label>
              <input
                type="text"
                value={branding.reportHeader}
                onChange={(e) => {
                  setBranding(prev => ({ ...prev, reportHeader: e.target.value }));
                  setHasChanges(true);
                }}
                className="w-full px-4 py-2 border rounded-lg"
                data-testid="report-header-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Report Footer Text</label>
              <input
                type="text"
                value={branding.reportFooter}
                onChange={(e) => {
                  setBranding(prev => ({ ...prev, reportFooter: e.target.value }));
                  setHasChanges(true);
                }}
                className="w-full px-4 py-2 border rounded-lg"
                data-testid="report-footer-input"
              />
            </div>
          </div>
        </div>

        {/* Localization */}
        <div className="bg-[var(--clr-surface-card)] rounded-xl border shadow-[var(--shadow-card)]">
          <div className="p-4 border-b">
            <h2 className="text-lg font-semibold text-[var(--clr-text-primary)]">Localization</h2>
          </div>
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Date Format</label>
              <select
                value={branding.dateFormat}
                onChange={(e) => {
                  setBranding(prev => ({ ...prev, dateFormat: e.target.value }));
                  setHasChanges(true);
                }}
                className="w-full px-4 py-2 border rounded-lg"
                data-testid="date-format-select"
              >
                <option value="DD/MM/YYYY">DD/MM/YYYY (31/12/2024)</option>
                <option value="MM/DD/YYYY">MM/DD/YYYY (12/31/2024)</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD (2024-12-31)</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
              <select
                value={branding.timezone}
                onChange={(e) => {
                  setBranding(prev => ({ ...prev, timezone: e.target.value }));
                  setHasChanges(true);
                }}
                className="w-full px-4 py-2 border rounded-lg"
                data-testid="timezone-select"
              >
                <option value="Africa/Johannesburg">Africa/Johannesburg (SAST)</option>
                <option value="Europe/London">Europe/London (GMT)</option>
                <option value="America/New_York">America/New York (EST)</option>
                <option value="Asia/Singapore">Asia/Singapore (SGT)</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="bg-[var(--clr-surface-card)] rounded-xl border shadow-[var(--shadow-card)]">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[var(--clr-text-primary)] flex items-center gap-2">
            <Eye className="w-5 h-5 text-[var(--clr-text-secondary)]" />
            Preview
          </h2>
        </div>
        <div className="p-6">
          <div className="border rounded-lg overflow-hidden">
            {/* Preview Header */}
            <div 
              className="p-4 text-white flex items-center justify-between"
              style={{ backgroundColor: branding.primaryColor }}
            >
              <div className="flex items-center gap-3">
                {branding.logoPreview && (
                  <img src={branding.logoPreview} alt="Logo" className="w-8 h-8 object-contain bg-[var(--clr-surface-card)] rounded" />
                )}
                <span className="font-semibold">{branding.companyName}</span>
              </div>
              <div className="flex gap-2">
                <div 
                  className="w-6 h-6 rounded"
                  style={{ backgroundColor: branding.secondaryColor }}
                />
                <div 
                  className="w-6 h-6 rounded"
                  style={{ backgroundColor: branding.accentColor }}
                />
              </div>
            </div>
            {/* Preview Content */}
            <div className="p-4 bg-[var(--clr-surface-subtle)]">
              <p className="text-sm text-[var(--clr-text-secondary)]">Sample content area with your branding applied</p>
            </div>
            {/* Preview Footer */}
            <div className="p-3 bg-[var(--clr-surface-subtle)] text-center text-xs text-[var(--clr-text-muted)]">
              {branding.reportFooter}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BrandThemingPage;
